import { Controller, useFormContext } from "react-hook-form";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import PropTypes from "prop-types";
import PublicIcon from '@mui/icons-material/Public';
import React, { useState } from "react";
import _ from "lodash";
import EditComponentTranslationOverlayWrapper from "@/Components/EditComponentTranslation/Containers/EditComponentTranslationOverlayWrapper";

InputText.propTypes = {
    name: PropTypes.string,
    translationComponent: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    rows: PropTypes.number,
    onBlurOverwrite: PropTypes.func,
    onChangeOverwrite: PropTypes.func,

    // Use this if a translation has an individual key like <COMPONENT>.Lipperland for example
    uniqueTranslationName: PropTypes.string
};

// @ts-ignore
export default function InputText({
    name,
    label,
    translationComponent,
    disabled,
    type = "default",
    rows = 1,
    onBlurOverwrite,
    onChangeOverwrite,
    uniqueTranslationName
}) {
    const [shouldOnBlurActionOnLeave, setShouldOnBlurActionOnLeave] =
        useState(false);

    const [translationPopupIsOpen, setTranslationPopupIsOpen] = useState(false);

    const editTranslationDisabled = import.meta.env.VITE_DISABLE_EDIT_TRANLATION_COMPONENT;

    const {
        control,
        formState: { isSubmitting, dirtyFields },
    } = useFormContext();

    const renderTranslationGlobe = () => {
        if (!translationComponent || editTranslationDisabled == "true") {
            return <></>
        }

        return (
            <InputAdornment position="end">
                <IconButton
                    size={"small"}
                    aria-label="toggle password visibility"
                    onClick={() => {
                        setTranslationPopupIsOpen(true)
                    }}
                    edge="end"
                >
                    <PublicIcon  />
                </IconButton>
            </InputAdornment>
        )
    }

    const renderTranslationComponentPopup = () => {
        if (!translationPopupIsOpen) {
            return <></>
        }

        return (
            <EditComponentTranslationOverlayWrapper
                isOpen={translationPopupIsOpen}
                setIsOpen={setTranslationPopupIsOpen}
                translationValue={
                    translationComponent + '.' + (uniqueTranslationName ?? name)
                }
                lang={"de"}
                edges={undefined}
            />
        ); 
    }

    return (
        <>
            {renderTranslationComponentPopup()}
            <Controller
                name={name}
                control={control}
                render={({
                    field: { onChange, value },
                    fieldState: { error, isDirty, invalid },
                }) => (
                    <TextField
                        data-testid={`${name}-input`}
                        onBlur={() => {
                            if (shouldOnBlurActionOnLeave && !invalid) {
                                if (_.isFunction(onBlurOverwrite)) {
                                    onBlurOverwrite();
                                    setShouldOnBlurActionOnLeave(false);
                                }
                            }
                        }}
                        label={label}
                        fullWidth
                        size={"small"}
                        autoComplete={"off"}
                        InputProps={{
                            autoComplete: "off", // disable autocomplete and autofill
                            endAdornment: (
                                renderTranslationGlobe()
                            ),
                        }}
                        rows={rows}
                        multiline={rows > 1}
                        onChange={(event) => {
                            // TextField has a type attribute, but it's pretty limited
                            // (numeric only type 'number' also allows for floats)
                            // This approach gives more control over what is actually allowed
                            if (type === "integer") {
                                const regex = /^[0-9]+$/;
                                if (
                                    event.target.value !== "" &&
                                    !regex.test(event.target.value)
                                ) {
                                    event.target.value = value;
                                }
                            }

                            if (_.isFunction(onChangeOverwrite)) {
                                onChangeOverwrite(event);
                            }

                        onChange(event);
                        setShouldOnBlurActionOnLeave(true);
                    }}
                    error={!!error}
                    disabled={isSubmitting || disabled}
                    value={value ?? ""}
                    helperText={error?.message}
                    margin={"dense"}
                />
            )}
        />
        </>
    );
}
